import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { BotServiceStatus, BotStatusRecord } from '../types/api.js';
import { ErrorState } from '../components/feedback/ErrorState/index.js';
import {
  collectHostWarnings,
  evaluateFreshness,
  evaluateOverall,
  evaluateService,
  type HealthLevel,
  type HealthReason,
} from '../components/features/bot-status/bot-status-health.js';
import {
  describeDuration,
  describeHostWarning,
  describeReason,
} from '../components/features/bot-status/bot-status-messages.js';
import { useBotStatus } from '../hooks/useBotStatus.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 母艦 bot 稼働状況ページ。母艦の collector が 5 分ごとに push するレポートを表示し、bot の停止・停滞・ホスト逼迫を一覧で確認できる。定期ジョブの健全性はホスト欄の backupAgeHours 経由でのみ確認できる。認証済み社員全員が閲覧可。
 * @graph-connects useBotStatus [calls] 最新レポート取得フック
 * @graph-connects evaluateOverall [calls] 全体健全性の判定
 * @graph-connects evaluateService [calls] サービス単位の健全性判定
 * @graph-connects describeReason [calls] 判定理由の翻訳
 */
export const Route = createFileRoute('/bot-status')({
  component: BotStatusPage,
});

/** 健全性に対応する表示色と翻訳キー。 @graph-connects none */
const LEVEL_STYLE: Record<HealthLevel, { dot: string; text: string; labelKey: string }> = {
  ok: {
    dot: 'bg-[var(--color-success)]',
    text: 'text-[var(--color-success)]',
    labelKey: 'botStatus.level.ok',
  },
  warn: {
    dot: 'bg-[var(--color-warning)]',
    text: 'text-[var(--color-warning)]',
    labelKey: 'botStatus.level.warn',
  },
  down: {
    dot: 'bg-[var(--color-error)]',
    text: 'text-[var(--color-error)]',
    labelKey: 'botStatus.level.down',
  },
  unknown: {
    dot: 'bg-[var(--color-text-muted)]',
    text: 'text-[var(--color-text-muted)]',
    labelKey: 'botStatus.level.unknown',
  },
};

/** バイト数を GB 表記にする。 @graph-connects none */
function toGb(bytes: number | undefined): string {
  if (bytes === undefined) return '-';
  return `${(bytes / 1024 ** 3).toFixed(1)}GB`;
}

/** 状態ドット + ラベル。 @graph-connects none */
function HealthBadge({ level, reason }: { level: HealthLevel; reason: HealthReason }) {
  const { t } = useTranslation();
  const style = LEVEL_STYLE[level];
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${style.dot}`} aria-hidden="true" />
      <span className={`text-sm ${style.text}`}>{t(style.labelKey)}</span>
      <span className="text-xs text-[var(--color-text-muted)]">{describeReason(t, reason)}</span>
    </span>
  );
}

/** サービス一覧の 1 セクション。 @graph-connects none */
function ServiceTable({
  title,
  services,
  serverTime,
}: {
  title: string;
  services: BotServiceStatus[];
  serverTime: string;
}) {
  const { t } = useTranslation();
  if (services.length === 0) return null;
  return (
    <section className="glass-card rounded-xl p-5">
      <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] mb-3">{title}</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-label={title}>
          <thead>
            <tr className="text-left text-xs text-[var(--color-text-muted)]">
              <th className="pb-2 pr-4 font-normal">{t('botStatus.columnName')}</th>
              <th className="pb-2 pr-4 font-normal">{t('botStatus.columnUser')}</th>
              <th className="pb-2 pr-4 font-normal">{t('botStatus.columnStatus')}</th>
              <th className="pb-2 font-normal">{t('botStatus.columnDetail')}</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => {
              const health = evaluateService(service, serverTime);
              return (
                <tr
                  key={`${service.user}/${service.name}`}
                  className="border-t border-[var(--color-border)]"
                >
                  <td className="py-2 pr-4 font-mono text-[var(--color-text-primary)]">
                    {service.name}
                  </td>
                  <td className="py-2 pr-4 text-[var(--color-text-secondary)]">{service.user}</td>
                  <td className="py-2 pr-4">
                    <HealthBadge level={health.level} reason={health.reason} />
                  </td>
                  <td className="py-2 text-xs text-[var(--color-text-muted)]">
                    {service.detail ?? ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** ホスト状態カード。 @graph-connects none */
function HostMetricsCard({ record }: { record: BotStatusRecord }) {
  const { t } = useTranslation();
  const metrics = record.hostMetrics;
  if (!metrics) return null;
  const warnings = collectHostWarnings(record).map((warning) => describeHostWarning(t, warning));
  const backup =
    metrics.backupAgeHours === null || metrics.backupAgeHours === undefined
      ? t('botStatus.backupUnmeasured', {
          source: metrics.backupSource ?? t('botStatus.unknownSource'),
        })
      : t('botStatus.backupAgo', { hours: metrics.backupAgeHours });

  return (
    <section className="glass-card rounded-xl p-5">
      <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] mb-3">
        {t('botStatus.hostTitle')}
      </h2>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-[var(--color-text-muted)]">{t('botStatus.memoryFree')}</dt>
          <dd className="text-[var(--color-text-primary)]">
            {toGb(metrics.memoryFreeBytes)} / {toGb(metrics.memoryTotalBytes)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-text-muted)]">swap</dt>
          <dd className="text-[var(--color-text-primary)]">
            {toGb(metrics.swapUsedBytes)} / {toGb(metrics.swapTotalBytes)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-text-muted)]">{t('botStatus.diskFree')}</dt>
          <dd className="text-[var(--color-text-primary)]">{toGb(metrics.diskFreeBytes)}</dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-text-muted)]">{t('botStatus.backup')}</dt>
          <dd className="text-[var(--color-text-primary)]">{backup}</dd>
        </div>
      </dl>
      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1">
          {warnings.map((warning) => (
            <li key={warning} className="text-sm text-[var(--color-warning)]">
              ⚠ {warning}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** @graph-connects none */
function BotStatusPage() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useBotStatus();

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('nav.botStatus')}
        </h1>
        <div className="glass-card p-6 rounded-xl">
          <div className="h-64 skeleton-glass rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        message={t('common.error')}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const record = data?.record ?? null;
  const serverTime = data?.serverTime ?? new Date().toISOString();
  const overall = evaluateOverall(record, serverTime);
  const freshness = evaluateFreshness(record, serverTime);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('nav.botStatus')}
        </h1>
        <HealthBadge level={overall.level} reason={overall.reason} />
      </div>

      {/* 鮮度は常に最初に出す。中身が全て正常でも、レポート自体が古ければ信用できない。 */}
      <section className="glass-card rounded-xl p-5">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <span className="text-[var(--color-text-secondary)]">
            {t('botStatus.lastReceived')}
            <span className={LEVEL_STYLE[freshness.level].text}>
              {freshness.ageMs === null
                ? '—'
                : t('botStatus.ago', { duration: describeDuration(t, freshness.ageMs) })}
            </span>
          </span>
          {record && (
            <>
              <span className="text-xs text-[var(--color-text-muted)]">
                host: <span className="font-mono">{record.host}</span>
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                collector v{record.collectorVersion ?? '-'}
              </span>
            </>
          )}
        </div>
      </section>

      {record && (
        <>
          {/* LaunchDaemon は表示しない。bot-* の launchd 状態は tmux を作って即 exit するため
              常に "not running" で健全性を表さず、ttyd / docs 等は個人のもので閲覧者に関係が無い。
              tm-unlock / check-backup-freshness が運ぶ唯一の実信号（バックアップ停止）は
              ホスト欄の backupAgeHours に入っている。collector 側も送信をやめているが、
              旧 collector からの launchd 混在データが届いても bot 表に紛れないようフィルタは残す。 */}
          <ServiceTable
            title="bot"
            services={record.services.filter((service) => service.kind === 'tmux')}
            serverTime={serverTime}
          />
          <HostMetricsCard record={record} />
        </>
      )}
    </div>
  );
}
